import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { lockPageScroll, scrollTo, scrollToSection } from '../utils/smoothScroll';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const releaseScrollRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) releaseScrollRef.current = lockPageScroll();
    return () => {
      releaseScrollRef.current?.();
      releaseScrollRef.current = null;
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    // Release synchronously so a CTA can replace inertia in this same click.
    releaseScrollRef.current?.();
    releaseScrollRef.current = null;
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    releaseScrollRef.current?.();
    releaseScrollRef.current = null;
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia?.('(min-width: 1024px)');
    if (!desktop) return;
    const closeOnDesktop = () => { if (desktop.matches) setIsMobileMenuOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  const goToSection = id => {
    closeMobileMenu();
    scrollToSection(id);
  };

  const navLinks = [
    { label: 'Insight', path: '/insight', isPage: true },
    { label: 'Chi Sono', path: '/chi-sono', isPage: true },
    { label: 'Metodo', path: '/metodo', isPage: true },
    { label: 'Servizi', path: '/servizi', isPage: true },
    { label: 'Risultati', path: '/risultati', isPage: true },
  ];

  const handleNavClick = (link) => {
    closeMobileMenu();
    navigate(link.path);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#0b114e] sm:bg-[#0b114e]/95 sm:backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <button
              onClick={() => {
                closeMobileMenu();
                if (!isHomePage) {
                  navigate('/');
                } else {
                  scrollTo(0);
                }
              }}
              className="flex items-center relative z-50"
            >
              <img 
                src="/media/nicola-logo.svg"
                alt="Nicola Scerni"
                className="h-12 sm:h-16 w-auto brightness-0 invert"
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleNavClick(link)}
                  className="text-white/80 hover:text-white text-sm font-medium transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => goToSection('contact')}
                className="bg-[#d4883a] hover:bg-[#d4896a] text-white text-sm font-semibold px-6 py-3 rounded-full transition-all"
              >
                Richiedi una Consulenza
              </button>
            </div>

            {/* Mobile Menu Button - Animated Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden relative z-50 w-10 h-10 flex items-center justify-center"
              aria-label="Menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                {/* Top line */}
                <span 
                  className={`block h-0.5 bg-white rounded-full transform transition-all duration-300 ease-out origin-center ${
                    isMobileMenuOpen 
                      ? 'rotate-45 translate-y-[9px]' 
                      : 'rotate-0 translate-y-0'
                  }`}
                />
                {/* Middle line */}
                <span 
                  className={`block h-0.5 bg-white rounded-full transition-all duration-200 ${
                    isMobileMenuOpen 
                      ? 'opacity-0 scale-x-0' 
                      : 'opacity-100 scale-x-100'
                  }`}
                />
                {/* Bottom line */}
                <span 
                  className={`block h-0.5 bg-white rounded-full transform transition-all duration-300 ease-out origin-center ${
                    isMobileMenuOpen 
                      ? '-rotate-45 -translate-y-[9px]' 
                      : 'rotate-0 translate-y-0'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Fullscreen Mobile Menu */}
      <div 
        data-lenis-prevent
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ease-out ${
          isMobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Background */}
        <div 
          className={`absolute inset-0 bg-[#0b114e] transition-transform duration-500 ease-out ${
            isMobileMenuOpen ? 'scale-100' : 'scale-95'
          }`}
        />
        
        {/* Decorative elements */}
        <div className="absolute top-1/4 -left-20 w-64 h-64 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        
        {/* Menu Content */}
        <div className="relative h-full flex flex-col justify-center items-center px-6">
          {/* Nav Links */}
          <nav className="flex flex-col items-center gap-2">
            {navLinks.map((link, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(link)}
                className={`text-white text-3xl sm:text-4xl font-bold py-3 transition-all duration-500 hover:text-[#d4883a] ${
                  isMobileMenuOpen 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
                style={{ 
                  transitionDelay: isMobileMenuOpen ? `${150 + index * 75}ms` : '0ms',
                  fontFamily: 'Georgia, serif'
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div 
            className={`mt-10 transition-all duration-500 ${
              isMobileMenuOpen 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: isMobileMenuOpen ? '550ms' : '0ms' }}
          >
            <button
              onClick={() => goToSection('contact')}
              className="bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all"
            >
              Richiedi una Consulenza
            </button>
          </div>

          {/* Bottom Info */}
          <div 
            className={`absolute bottom-8 left-0 right-0 text-center transition-all duration-500 ${
              isMobileMenuOpen 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: isMobileMenuOpen ? '650ms' : '0ms' }}
          >
            <p className="text-white/40 text-sm">info@nicolascerni.it</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
